import { Field } from '@amore/ui/components/field'
import { Input } from '@amore/ui/components/input'
import { InputAddon, InputGroup } from '@amore/ui/components/input-group'
import { useState } from 'react'
import { formatMoney, plural } from '@/lib/format'
import { useUpdate, useWedding } from '@/mock/store'
import type { Expense, WeddingData } from '@/mock/types'
import { ConfirmDialog, FormDialog } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { budgetTotals, expenseTitle } from './model'

/**
 * "Nastavit rozpočet": the one amount the couple wants to spend. Not a record,
 * so a small dialog rather than a sheet. Setting it finishes the automatic
 * "Stanovit rámcový rozpočet" task.
 */
export function TotalDialog({ onClose }: { onClose: () => void }) {
  const data = useWedding()
  const update = useUpdate()
  const { showToast } = useToast()
  const current = data.budget.total
  const [value, setValue] = useState(current ? String(current) : '')
  const [error, setError] = useState(false)
  const amount = Number(value) || 0
  const { planned } = budgetTotals(data)

  const save = () => {
    if (amount <= 0) {
      setError(true)
      return
    }
    update((wedding) => ({
      ...wedding,
      budget: { ...wedding.budget, total: amount },
      tasks: wedding.tasks.map((task) =>
        task.automatic === 'budget-total' ? { ...task, done: true } : task,
      ),
    }))
    showToast({
      title: current === null ? 'Rozpočet nastaven' : 'Rozpočet upraven',
      description: formatMoney(amount),
      tone: 'success',
    })
    onClose()
  }

  return (
    <FormDialog
      open
      width="compact"
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={current === null ? 'Nastavit rozpočet' : 'Upravit rozpočet'}
      description="Kolik chcete za svatbu celkem utratit. Položky ani platby se tím nezmění."
      dirty={value !== (current ? String(current) : '')}
      primaryAction={{
        label: current === null ? 'Nastavit rozpočet' : 'Uložit',
        onClick: save,
      }}
    >
      <Field
        label="Celkový rozpočet"
        error={error ? 'Zadejte částku, kterou chcete mít k dispozici.' : undefined}
        description={
          planned > 0
            ? amount > 0 && amount < planned
              ? `Naplánováno je už ${formatMoney(planned)}, rozpočet by byl přečerpaný o ${formatMoney(planned - amount)}.`
              : `Zatím naplánováno ${formatMoney(planned)}.`
            : undefined
        }
      >
        <InputGroup>
          <Input
            value={value ? Number(value).toLocaleString('cs-CZ') : ''}
            inputMode="numeric"
            placeholder="Např. 450 000"
            aria-invalid={error || undefined}
            className="tabular-nums"
            onChange={(event) => {
              setValue(event.target.value.replace(/\D/g, ''))
              setError(false)
            }}
          />
          <InputAddon side="end">Kč</InputAddon>
        </InputGroup>
      </Field>
    </FormDialog>
  )
}

/** Deletes an item with its payments and payment tasks; the supplier stays in My suppliers. */
export function removeExpense(data: WeddingData, expenseId: string): WeddingData {
  return {
    ...data,
    budget: {
      ...data.budget,
      expenses: data.budget.expenses.filter((expense) => expense.id !== expenseId),
    },
    tasks: data.tasks.filter((task) => task.payment?.expenseId !== expenseId),
  }
}

/** "Smazat položku?": says what goes with it, deletes it and offers undo. */
export function DeleteExpenseDialog({
  expense,
  open,
  onOpenChange,
  onDeleted,
}: {
  expense: Expense
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted: () => void
}) {
  const data = useWedding()
  const update = useUpdate()
  const { showToast } = useToast()
  const title = expenseTitle(expense, data)
  const payments = expense.payments.length
  const tasks = data.tasks.filter((task) => task.payment?.expenseId === expense.id).length
  const consequences = [
    payments
      ? `${payments === 1 ? 'Smaže se i její' : 'Smažou se i její'} ${payments} ${plural(payments, 'platba', 'platby', 'plateb')}${tasks ? ` a ${tasks === 1 ? 'úkol' : 'úkoly'} k zaplacení v Úkolech` : ''}.`
      : null,
    expense.supplierId ? 'Dodavatel zůstane v Mých dodavatelích.' : null,
  ].filter(Boolean)

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      heading={`Smazat položku ${title}?`}
      description={consequences.length ? consequences.join(' ') : 'Tuto akci můžete hned vrátit.'}
      keepLabel="Ponechat položku"
      confirmLabel="Smazat položku"
      onConfirm={() => {
        const previous = data
        update((current) => removeExpense(current, expense.id))
        showToast({
          title: 'Položka smazána',
          description: title,
          action: {
            label: 'Vrátit zpět',
            altText: 'Vrátit smazanou položku',
            onClick: () => update(() => previous),
          },
        })
        onDeleted()
      }}
    />
  )
}
